import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, X, Quote, Pencil, Trash2, Archive } from 'lucide-react'
import { api } from '../api'
import type { Role, Template } from '../types'
import { atLeast } from '../types'
import {
  Button, Card, CardTitle, EmptyState, Field, Input, Textarea, Badge, SectionLabel,
  ErrorText, Menu, Dialog } from '../components/ui'
import { Select, Tabs } from '../components/select'
import { wordCount } from '../lib/utils'

const KINDS = ['hook', 'cta'] as const
type VisibleTemplateKind = typeof KINDS[number]

const KIND_EMPTY: Record<string, string> = {
  hook: 'Add the openings that have worked. A draft fills the placeholders in with real specifics from the brand context.',
  cta: 'Add the closings that have worked. The best ones call back to the hook.',
}

const KIND_BLURB: Record<string, string> = {
  hook: 'Opens the video. Someone is scrolling — this is what stops them.',
  cta: 'Closes. Best when it calls back to the hook.',
}

export function Templates({ ws, role, kind, adding: controlledAdding, onAddingChange, hideAction = false }: {
  ws: string
  role?: Role
  kind?: VisibleTemplateKind
  adding?: boolean
  onAddingChange?: (adding: boolean) => void
  hideAction?: boolean
}) {
  const qc = useQueryClient()
  const [localAdding, setLocalAdding] = useState(false)
  const adding = controlledAdding ?? localAdding
  const setAdding = (next: boolean) => {
    setLocalAdding(next)
    onAddingChange?.(next)
  }
  const [tab, setTab] = useState<VisibleTemplateKind>(kind ?? 'hook')
  const templates = useQuery({
    queryKey: ['templates', ws],
    queryFn: () => api.get<Template[]>(`/api/workspaces/${ws}/templates`),
  })
  const canEdit = atLeast(role, 'member')

  useEffect(() => {
    if (kind) setTab(kind)
    setAdding(false)
  }, [kind])

  const rows = templates.data ?? []
  const shown = rows.filter(t => t.kind === tab)

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {!kind && (
          <Tabs
            value={tab}
            onValueChange={v => setTab(v as VisibleTemplateKind)}
            tabs={KINDS.map(k => ({
              value: k,
              label: `${k[0].toUpperCase()}${k.slice(1)}s`,
              count: rows.filter(t => t.kind === k).length,
            }))}
          />
        )}
        {canEdit && !hideAction && (
          <Button variant={adding ? 'ghost' : 'primary'} onClick={() => setAdding(!adding)}>
            {adding ? <><X /> Cancel</> : <><Plus /> New {tab === 'cta' ? 'CTA' : tab}</>}
          </Button>
        )}
      </div>

      <p className="text-xs text-ink-faint">{KIND_BLURB[tab]}</p>

      {adding && (
        // Opens on the kind you are already looking at.
        <NewTemplate
          ws={ws} initialKind={tab}
          onDone={() => { setAdding(false); qc.invalidateQueries({ queryKey: ['templates', ws] }) }}
        />
      )}

      <ErrorText error={templates.error} />

      {shown.length === 0 && !adding && (
        <EmptyState
          icon={Quote}
          title={`No ${tab}s yet`}
          body={KIND_EMPTY[tab]}
          action={canEdit && !hideAction
            ? <Button variant="primary" onClick={() => setAdding(true)}>
              <Plus /> New {tab === 'cta' ? 'CTA' : tab}
            </Button>
            : undefined}
        />
      )}

      {/* Grouped by category rather than one flat alphabetical run. With the
          default library in place this list is twenty-odd items, and the
          categories are the useful unit: you arrive wanting "something that
          stops the scroll", not a name you already know. */}
      <div className="space-y-6">
        {groupByCategory(shown).map(([category, items]) => (
          <div key={category} className="space-y-2">
            {/* Only worth a heading when there is more than one group --
                otherwise it is a label over the entire page. */}
            {groupByCategory(shown).length > 1 && (
              <SectionLabel>{category} · {items.length}</SectionLabel>
            )}
            {items.map(t => <TemplateCard key={t.id} ws={ws} template={t} canEdit={canEdit} />)}
          </div>
        ))}
      </div>
    </div>
  )
}

function TemplateCard({ ws, template, canEdit }: { ws: string; template: Template; canEdit: boolean }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(template.name)
  const [pattern, setPattern] = useState(template.pattern)
  const [category, setCategory] = useState(template.category ?? '')
  const invalidate = () => qc.invalidateQueries({ queryKey: ['templates', ws] })
  const save = useMutation({
    mutationFn: () => api.patch(`/api/workspaces/${ws}/templates/${template.id}`, {
      name: name.trim(), pattern: pattern.trim(), category: category.trim() || null,
    }),
    onSuccess: () => { setEditing(false); invalidate() },
  })
  const archive = useMutation({
    mutationFn: () => api.post(`/api/workspaces/${ws}/templates/${template.id}/archive`),
    onSuccess: invalidate,
  })
  const remove = useMutation({
    mutationFn: () => api.del(`/api/workspaces/${ws}/templates/${template.id}`),
    onSuccess: invalidate,
  })

  return (
    <>
      <Card className="grid grid-cols-[minmax(7rem,0.28fr)_minmax(0,1fr)_auto] items-center gap-4 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{template.name}</span>
          {!template.active && <Badge tone="bad">archived</Badge>}
        </div>
        <p className="rounded-[--radius-control] bg-surface-2/60 p-3 font-mono text-[13px] leading-relaxed text-ink-dim">
          {template.pattern}
        </p>
        {canEdit && (
          <Menu
            label={`Actions for ${template.name}`}
            actions={[
              { label: 'Edit', icon: Pencil, onSelect: () => {
                setName(template.name); setPattern(template.pattern); setCategory(template.category ?? ''); setEditing(true)
              } },
              { label: 'Archive', icon: Archive, confirmLabel: `Archive ${template.name}? It will not be used in future videos.`, onSelect: () => archive.mutate() },
              { label: 'Delete', icon: Trash2, danger: true, confirmLabel: `Delete ${template.name}?`, onSelect: () => remove.mutate() },
            ]}
          />
        )}
      </Card>
      <ErrorText error={archive.error || remove.error} />
      <Dialog open={editing} onOpenChange={setEditing} title={`Edit ${template.kind === 'cta' ? 'CTA' : 'hook'}`}>
        <div className="space-y-4">
          <Field label="Name"><Input value={name} onChange={event => setName(event.target.value)} /></Field>
          <Field label="Category"><Input value={category} onChange={event => setCategory(event.target.value)} /></Field>
          <Field label="Pattern" help={`${wordCount(pattern)} words`}>
            <Textarea rows={4} value={pattern} onChange={event => setPattern(event.target.value)} className="font-mono text-[13px]" />
          </Field>
          <ErrorText error={save.error} />
          <div className="flex gap-2">
            <Button variant="primary" onClick={() => save.mutate()} disabled={!name.trim() || !pattern.trim() || save.isPending}>
              {save.isPending ? 'Saving…' : 'Save changes'}
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      </Dialog>
    </>
  )
}

/** Groups templates by category, preserving the order they arrive in.
 *
 *  Uncategorised ones collect under "Other" at the end rather than being
 *  hidden or given a heading of their own each: a hand-written template with
 *  no category is still a template you need to find. */
function groupByCategory(list: Template[]): [string, Template[]][] {
  const groups = new Map<string, Template[]>()
  for (const t of list) {
    const key = t.category?.trim() || 'Other'
    const existing = groups.get(key)
    if (existing) existing.push(t)
    else groups.set(key, [t])
  }
  const other = groups.get('Other')
  groups.delete('Other')
  const out = [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  if (other) out.push(['Other', other])
  return out
}

function NewTemplate({ ws, initialKind, onDone }: {
  ws: string
  initialKind: VisibleTemplateKind
  onDone: () => void
}) {
  const [kind, setKind] = useState<VisibleTemplateKind>(initialKind)
  const [name, setName] = useState('')
  const [pattern, setPattern] = useState('')
  const [category, setCategory] = useState('')

  const create = useMutation({
    mutationFn: () => api.post(`/api/workspaces/${ws}/templates`, {
      kind, name, pattern, category: category || null,
    }),
    onSuccess: onDone,
  })

  const words = wordCount(pattern)

  return (
    <Card className="space-y-4 border-brand/25">
      <CardTitle>New template</CardTitle>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Kind">
          <Select
            value={kind} onValueChange={v => setKind(v as VisibleTemplateKind)}
            items={KINDS.map(k => ({ value: k, label: k, hint: KIND_BLURB[k] }))}
          />
        </Field>
        <Field label="Name">
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Time drain" />
        </Field>
        <Field label="Category">
          <Input
            value={category} onChange={e => setCategory(e.target.value)}
            placeholder="before/after"
          />
        </Field>
      </div>

      <Field
        label="Pattern"
        help={`About ${words} words before filling. A finished line wants ~23 to fill a 10s clip — leave room for the placeholders.`}
      >
        <Textarea
          rows={3} value={pattern} onChange={e => setPattern(e.target.value)}
          placeholder="I used to {pain} — now {product} does it in {time}."
          className="font-mono text-[13px]"
        />
      </Field>

      <ErrorText error={create.error} />
      <Button variant="primary" onClick={() => create.mutate()} disabled={!name || !pattern || create.isPending}>
        {create.isPending ? 'Saving…' : 'Create template'}
      </Button>
    </Card>
  )
}
